import React, { useMemo, useCallback } from 'react';
import { Row, Col, Button } from 'antd';
import { PlusCircleOutlined, SnippetsOutlined } from '@ant-design/icons';
import _ from 'lodash';

import { AddsContainer } from './common';
import { ReactSortable } from 'react-sortablejs';

import Labor from './Labor';
import { CurrentUserStore } from 'stores';
import {
  createHandleDragEnd,
  onlyAllowType,
} from '../util/CreateHandleDragEnd';
import EmptyItem from './EmptyItem';
import { v4 as uuidv4 } from 'uuid';

const MemoizedEmptyItem = React.memo(EmptyItem);

const LaborsContainer = React.memo(function LaborsContainer({
  itemKey,
  labours,
  addSubItem,
  deleteSubItem,
  changeSubItem,
  moveItem,
}) {
  const currentUserSettings = CurrentUserStore.user.settings;

  const handleDragEnd = useMemo(
    () =>
      createHandleDragEnd({
        items: labours,
        type: 'labours',
        itemKey,
        moveItem,
      }),
    [labours, itemKey, moveItem]
  );

  const sortedLabours = useMemo(
    () =>
      [...(labours || [])].sort(
        (a, b) => (a.position ?? 0) - (b.position ?? 0)
      ),
    [labours]
  );

  const handleAddLabour = useCallback(() => {
    const maxPosition = sortedLabours.reduce(
      (max, labour) => Math.max(max, labour.position ?? 0),
      0
    );
    addSubItem('labours', {
      key: uuidv4(),
      id: _.random(100000),
      vendor: '',
      description: '',
      hours: 0,
      cost: currentUserSettings?.laborRate || 0,
      amount: 0,
      position: maxPosition + 1,
    });
  }, [addSubItem, sortedLabours, currentUserSettings]);

  const setListGuardRef = React.useRef({});

  return (
    <>
      <Row
        style={{
          margin: '10px',
          padding: labours.length === 0 ? '0px 20px' : 0,
        }}
        gutter={16}
        className="labours-container"
        dir="vertical"
        data-lineitem={itemKey.toString()}
      >
        <Col span={24}>
          <AddsContainer>
            <ReactSortable
              tag="div"
              list={labours || []}
              setList={(newList) => {
                if (!setListGuardRef.current[itemKey]) {
                  setListGuardRef.current[itemKey] = true;
                  return;
                }
                moveItem(itemKey, 'labours', newList);
              }}
              onEnd={handleDragEnd}
              group={{
                name: 'sharedLabours',
                pull: true,
                put: onlyAllowType('labours'),
              }}
              animation={150}
              data-lineitem={itemKey.toString()}
              scroll={true}
              scrollSensitivity={100}
              scrollSpeed={10}
              forceFallback={true}
              scrollTarget={() => document.querySelector('.wrapper-wo')}
              ghostClass="ghost"
              chosenClass="chosen"
              dragClass="dragging"
              tagProps={{ 'data-lineitem': itemKey }}
            >
              {sortedLabours.length === 0 ? (
                <MemoizedEmptyItem message="Drag and drop misc items here or add manually" />
              ) : (
                sortedLabours.map((item) => (
                  <div
                    data-type={'labours'}
                    key={item.key}
                    data-id={item.key.toString()}
                  >
                    <Labor
                      item={item}
                      key={item.key}
                      deleteSubItem={() =>
                        deleteSubItem(itemKey, 'labours', item.key)
                      }
                      changeSubItem={(value) =>
                        changeSubItem(itemKey, 'labours', item.key, value)
                      }
                    />
                  </div>
                ))
              )}
            </ReactSortable>
          </AddsContainer>
          <Row>
            <Col
              style={{ width: 40, textAlign: 'center', marginTop: '20px' }}
            />
            <Col style={{ flex: 1 }}>
              <Button
                type="link"
                onClick={handleAddLabour}
                icon={<PlusCircleOutlined />}
                data-testid="labors-container-add-labor-button"
              >
                Add Labor
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
});

export default LaborsContainer;
